import { useEffect } from 'react';
import { IonButton, IonContent, IonPage } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useInAppHistoryStore } from '../store/useInAppHistoryStore';

/**
 * Terminal page surfaced when an API call fails because the network is
 * unavailable, or when the browser fires an `offline` event.
 *
 * Exempt from all app gates (RegistrationGate, ProtectedRoute) — see App.tsx
 * — since the user can't pass any gate that needs the network.
 *
 * Smart return: auto-bounces back the moment the browser fires `online`,
 * and the Try Again button does the same on demand. Goes back to wherever
 * the user was, or home if there's no history.
 */
export const NoNetworkPage: React.FC = () => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const leave = () => {
    // `window.history.length` / `window.history.back()` are unsafe here:
    // length includes pre-app entries (popping off the site to wherever the
    // user came from), and on a fresh load / standalone PWA there may be no
    // navigable prior entry — `back()` then closes the tab. Use the in-app
    // depth counter so we only goBack when we know the previous entry is an
    // app route we pushed ourselves; otherwise replace into /.
    if (useInAppHistoryStore.getState().depth > 0) {
      history.goBack();
    } else {
      history.replace('/');
    }
  };

  useEffect(() => {
    // Steady-state check: if the user lands here (e.g. via refresh) with the
    // network already back, leave immediately — `online` only fires on a
    // state change, not on a fresh load that's already online.
    if (navigator.onLine) {
      leave();
      return;
    }
    const onOnline = () => leave();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const handleTryAgain = () => {
    if (!navigator.onLine) return;
    leave();
  };

  return (
    <IonPage>
      <IonContent>
        <div className="mx-auto max-w-md px-4 py-12 flex flex-col items-center gap-6 text-center">
          <h1 className="text-2xl font-bold text-foreground">{t('pages.noNetwork.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('pages.noNetwork.body')}
          </p>
          <IonButton expand="block" onClick={handleTryAgain} className="w-full">
            {t('actions.retry')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};
